import { Box, Slider, Tooltip, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import React, { useLayoutEffect, useMemo, useRef, useState } from "react";

import { monospaceStyle } from "../../theme";
import EntityLink from "../Detail/EntityLink";
import RowToggle from "../Detail/RowToggle";
import { quiet, tableFrame } from "../Detail/claimCells";
import RecordTooltip, { RECORD_TOOLTIP_SLOTS } from "./RecordTooltip";
import {
  DateSpan,
  IncidentMark,
  OTHER_LANE,
  OpenReach,
  ProgrammeMark,
  TimelineModel,
  YEAR,
  dateWords,
  yearTicks,
} from "./timelineModel";

export interface ProductTimelineProps {
  model: TimelineModel;
  // The time "now" stands at; the line of today is drawn there
  today?: number;
}

// Width (px) of the column of names, and the plot's width before it is
// measured (and where it cannot be, as in a test)
const NAMES = 200;
const FALLBACK_WIDTH = 640;
// Heights (px): a programme row, an incident lane, a bar
const ROW = 22;
const LANE = 24;
const BAR = 8;
// Least width (px) a mark is drawn at, and its hit target
const MIN_MARK = 8;
const HIT = 14;
// How far (px) a mentioned programme's band reaches past its first and last
// mention, fading; the diameter of a mention's dot; the middle of a day
const MENTION_REACH = 14;
const MENTION_DOT = 6;
const DAY_MIDDLE = 12 * 3600 * 1000;
// Programmes listed before the rest are folded away
const PROGRAMMES_SHOWN = 8;
// The axis offers a range to zoom into from a span of this many years
const ZOOM_FROM_YEARS = 4;

// Incident marks by severity: the graver, the larger (px)
const SEVERITY_SIZE: Record<string, number> = {
  minor: 7,
  moderate: 9,
  major: 11,
  critical: 13,
};
const DEFAULT_SIZE = 8;

const subheading = {
  ...quiet,
  fontSize: "0.6875rem",
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
} as const;

const yearOf = (time: number): number => new Date(time).getUTCFullYear();

// The width of an element, followed as it changes
const useWidth = (ref: React.RefObject<HTMLElement>): number => {
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) {
      return undefined;
    }
    setWidth(element.getBoundingClientRect().width);
    // Not every environment observes sizes: the test environment does not
    if (typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const observer = new ResizeObserver(([entry]) =>
      setWidth(entry.contentRect.width),
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);
  return width;
};

const dayWords = (time: number): string => new Date(time).toISOString().slice(0, 10);

// The days a mentioned programme's sources were published, first to last
const mentionWords = (mark: ProgrammeMark): string => {
  const first = dayWords(mark.mentions[0]);
  const last = dayWords(mark.mentions[mark.mentions.length - 1]);
  return first === last ? first : `${first} – ${last}`;
};

// A programme's date as its label reads it: the dates stated, or for one
// placed by its mentions, the days of the first and the last
const programmeDate = (mark: ProgrammeMark): string =>
  mark.shape === "mentioned" ? `mentioned ${mentionWords(mark)}` : dateWords(mark.row);

// How exactly a date is known, where it is not to the day
const precisionNote = (span: DateSpan): string | null =>
  span.precision === "day" ? null : `known to the ${span.precision}`;

// A programme's date line in its tooltip: its name, its value, and how it
// is known
const programmeDateLine = (
  mark: ProgrammeMark,
): { label: string; date: string; note: string | null } => {
  if (mark.shape === "mentioned") {
    const count = mark.mentions.length;
    return {
      label: "Mentioned",
      date: mentionWords(mark),
      note: `no dates stated · ${count} ${count === 1 ? "source" : "sources"}`,
    };
  }
  // An open programme is drawn solid as far as its newest source
  const newest =
    mark.shape === "open" && mark.mentions.length > 0
      ? `newest source ${dayWords(mark.mentions[mark.mentions.length - 1])}`
      : null;
  return {
    label: mark.end ? "Period" : "Start",
    date: dateWords(mark.row),
    note: [precisionNote(mark.start), newest].filter(Boolean).join(" · ") || null,
  };
};

/**
 * A product's programmes and incidents on one axis of time.
 *
 * Programmes above, a row each, earliest first: a bar from start to end; an
 * ongoing one with no end runs to its newest source and fades for a while
 * past it, no further than today; a planned one is
 * hollow; a completed one with no end marks its start alone; one no source
 * dates stands where its sources were published, a dot on each day over a
 * faint band that fades past the first and the last. A part of a bar the
 * dates know only to the month or the year is drawn faint, so that no date
 * looks more exact than it is. Incidents beneath, a lane per type:
 * a dot each, the graver the larger, or a faint bar across the year or
 * month a date is known to where that is wider than a dot. A line marks
 * today; past a span of some years a range picks the years shown. Each mark
 * names its record on hover.
 */
const ProductTimeline: React.FC<ProductTimelineProps> = ({
  model,
  today = Date.now(),
}) => {
  const theme = useTheme();
  const programmeHue = theme.palette.chart.timelineProgramme;
  const incidentHue = theme.palette.chart.timelineIncident;
  const surface = theme.palette.background.raised;

  const plotRef = useRef<HTMLDivElement>(null);
  const measured = useWidth(plotRef);
  const width = measured > 0 ? measured : FALLBACK_WIDTH;

  // The years the range can pick, and the ones it holds
  const firstYear = yearOf(model.from);
  const lastYear = yearOf(model.to) + 1;
  const zoomable = lastYear - firstYear >= ZOOM_FROM_YEARS;
  const [years, setYears] = useState<number[]>([firstYear, lastYear]);
  const from = zoomable ? Math.max(model.from, Date.UTC(years[0], 0, 1)) : model.from;
  const to = zoomable ? Math.min(model.to, Date.UTC(years[1], 0, 1)) : model.to;
  const span = Math.max(to - from, YEAR / 12);

  const [allProgrammes, setAllProgrammes] = useState(false);
  const programmes = allProgrammes
    ? model.programmes
    : model.programmes.slice(0, PROGRAMMES_SHOWN);
  const folded = model.programmes.length - programmes.length;

  const x = (time: number): number => ((time - from) / span) * width;
  // A stretch of time on the plot, cut to its edges; null when outside
  const stretch = (lo: number, hi: number): { left: number; width: number } | null => {
    const left = Math.max(x(lo), 0);
    const right = Math.min(x(hi), width);
    return right < 0 || left > width || right < left ? null : { left, width: right - left };
  };

  const ticks = useMemo(() => yearTicks(from, to), [from, to]);
  const todayX = x(today);

  // Drawn in each row by a call, not as a component of its own, so that a
  // render does not remount the marks and close their tooltips
  const todayLine = () =>
    todayX >= 0 && todayX <= width ? (
      <Box
        sx={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: todayX,
          borderLeft: "1px dashed",
          borderColor: alpha(theme.palette.chart.ink, 0.3),
          pointerEvents: "none",
        }}
      />
    ) : null;

  const gridlines = () => (
    <>
      {ticks.map((year) => {
        const left = x(Date.UTC(year, 0, 1));
        return left >= 0 && left <= width ? (
          <Box
            key={year}
            sx={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left,
              borderLeft: 1,
              borderColor: alpha(theme.palette.chart.ink, 0.05),
              pointerEvents: "none",
            }}
          />
        ) : null;
      })}
    </>
  );

  // A bar on the plot: solid, or faint where its date is uncertain
  const bar = (
    lo: number,
    hi: number,
    options: { faint?: boolean; fadeFrom?: number; hollow?: boolean; color: string },
  ): React.ReactNode => {
    const place = stretch(lo, hi);
    if (!place) {
      return null;
    }
    const drawn = Math.max(place.width, MIN_MARK);
    // Where along the bar (%) it begins to fade
    const solid =
      options.fadeFrom === undefined || place.width === 0
        ? null
        : Math.min(Math.max(((x(options.fadeFrom) - place.left) / place.width) * 100, 0), 100);
    return (
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: place.left - (drawn - place.width) / 2,
          width: drawn,
          height: BAR,
          transform: "translateY(-50%)",
          borderRadius: `${BAR / 2}px`,
          boxSizing: "border-box",
          ...(options.hollow
            ? { border: `1.5px dashed ${options.color}`, backgroundColor: alpha(options.color, 0.12) }
            : {
                background:
                  solid !== null
                    ? `linear-gradient(to right, ${options.color} ${solid}%, ${alpha(options.color, 0)})`
                    : options.color,
                opacity: options.faint ? 0.35 : 1,
              }),
        }}
      />
    );
  };

  const programmeMarks = (mark: ProgrammeMark): React.ReactNode => {
    const { start, end, shape } = mark;
    const color = programmeHue;
    switch (shape) {
      case "span": {
        const closing = end as DateSpan;
        return start.hi < closing.lo ? (
          <>
            {start.precision !== "day" && bar(start.lo, start.hi, { faint: true, color })}
            {bar(start.precision === "day" ? start.lo : start.hi, closing.lo, { color })}
            {closing.precision !== "day" && bar(closing.lo, closing.hi, { faint: true, color })}
            {closing.precision === "day" && bar(closing.lo, closing.hi, { color })}
          </>
        ) : (
          bar(start.lo, closing.hi, { color })
        );
      }
      case "open": {
        // Solid to its newest source, then fading; no fade where that is
        // today
        const reach = mark.reach as OpenReach;
        return (
          <>
            {start.precision !== "day" && bar(start.lo, start.hi, { faint: true, color })}
            {bar(start.precision === "day" ? start.lo : start.hi, reach.fadeTo, {
              fadeFrom: reach.fadeTo > reach.seen ? reach.seen : undefined,
              color,
            })}
          </>
        );
      }
      case "planned":
        return bar(start.lo, (end ?? start).hi, { hollow: true, color });
      case "point":
        return bar(start.lo, start.hi, { faint: start.precision !== "day", color });
      case "mentioned":
        return mentionedMarks(mark);
    }
  };

  // A programme no source dates: a faint band from its first mention to its
  // last, reaching a little past both and fading there, and a dot on each
  // day a source of it was published
  const mentionedMarks = (mark: ProgrammeMark): React.ReactNode => {
    const place = stretch(mark.start.lo, (mark.end ?? mark.start).hi);
    const left = place ? place.left - MENTION_REACH : null;
    const bandWidth = place ? place.width + 2 * MENTION_REACH : 0;
    const fadeShare = bandWidth > 0 ? (MENTION_REACH / bandWidth) * 100 : 0;
    const tint = alpha(programmeHue, 0.22);
    return (
      <>
        {left !== null && (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left,
              width: bandWidth,
              height: BAR,
              transform: "translateY(-50%)",
              borderRadius: `${BAR / 2}px`,
              background: `linear-gradient(to right, ${alpha(programmeHue, 0)}, ${tint} ${fadeShare}%, ${tint} ${100 - fadeShare}%, ${alpha(programmeHue, 0)})`,
            }}
          />
        )}
        {mark.mentions.map((day) => {
          const dot = x(day + DAY_MIDDLE);
          return dot >= 0 && dot <= width ? (
            <Box
              key={day}
              sx={{
                position: "absolute",
                top: "50%",
                left: dot,
                width: MENTION_DOT,
                height: MENTION_DOT,
                transform: "translate(-50%, -50%)",
                borderRadius: "50%",
                backgroundColor: programmeHue,
                boxShadow: `0 0 0 1.5px ${surface}`,
              }}
            />
          ) : null;
        })}
      </>
    );
  };

  // The stretch a programme's marks cover, for its hit target
  const programmeExtent = (mark: ProgrammeMark) =>
    stretch(
      mark.start.lo,
      mark.reach?.fadeTo ?? (mark.end ?? mark.start).hi,
    );

  const incidentMark = (mark: IncidentMark): React.ReactNode => {
    const size = SEVERITY_SIZE[mark.row.severity ?? ""] ?? DEFAULT_SIZE;
    const place = stretch(mark.at.lo, mark.at.hi);
    if (!place) {
      return null;
    }
    // A date known to a month or a year that is wider than a dot: a faint
    // bar across it, with the dot at its middle
    const wide = place.width > size * 1.5;
    const middle = place.left + place.width / 2;
    return (
      <>
        {wide && (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: place.left,
              width: place.width,
              height: size,
              transform: "translateY(-50%)",
              borderRadius: `${size / 2}px`,
              backgroundColor: alpha(incidentHue, 0.22),
            }}
          />
        )}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: middle,
            width: size,
            height: size,
            transform: "translate(-50%, -50%)",
            borderRadius: "50%",
            backgroundColor: wide ? alpha(incidentHue, 0.7) : incidentHue,
            // A ring of the surface parts marks that overlap
            boxShadow: `0 0 0 2px ${surface}`,
          }}
        />
      </>
    );
  };

  // The hit target of a mark, wider than the mark, with its tooltip
  const hit = (
    left: number,
    hitWidth: number,
    label: string,
    title: React.ReactNode,
  ): React.ReactNode => (
    <Tooltip
      title={title}
      placement="top"
      arrow
      disableInteractive
      enterDelay={80}
      slotProps={RECORD_TOOLTIP_SLOTS}
    >
      <Box
        aria-label={label}
        sx={{
          position: "absolute",
          top: "50%",
          left: left - Math.max(HIT - hitWidth, 0) / 2,
          width: Math.max(hitWidth, HIT),
          height: HIT + 4,
          transform: "translateY(-50%)",
        }}
      />
    </Tooltip>
  );

  // What a programme says on hover, over its marks and over its name alike
  const programmeTooltip = (mark: ProgrammeMark): React.ReactNode => {
    const line = programmeDateLine(mark);
    return (
      <RecordTooltip
        kind="programme"
        row={mark.row}
        hue={programmeHue}
        roles={mark.roles}
        dateLabel={line.label}
        date={line.date}
        dateNote={line.note}
      />
    );
  };

  const nameCell = { width: NAMES, flexShrink: 0, pr: 1.5, minWidth: 0 } as const;

  return (
    <Box sx={{ ...tableFrame, px: 1.75, pt: 1.25, pb: 1 }}>
      {/* The legend, and the range of years where the span is long */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          columnGap: 2,
          rowGap: 0.5,
          mb: 1,
          fontSize: "0.75rem",
          color: "text.secondary",
        }}
      >
        <LegendBar color={programmeHue} label="Programme" />
        <LegendBar color={programmeHue} label="no end: fades past its newest source" fade />
        <LegendBar color={programmeHue} label="planned" hollow />
        <LegendBar color={programmeHue} label="date known to the month or year" faint />
        <LegendMentions color={programmeHue} label="no dates stated: when sources mention it" />
        <LegendDots color={incidentHue} label="Incident, by severity" />
        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
          <Box
            component="span"
            sx={{ height: 12, borderLeft: "1px dashed", borderColor: alpha(theme.palette.chart.ink, 0.4) }}
          />
          today
        </Box>
        {zoomable && (
          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1.5, minWidth: 220 }}>
            <Typography variant="caption" sx={quiet}>
              Years
            </Typography>
            <Slider
              size="small"
              value={years}
              min={firstYear}
              max={lastYear}
              step={1}
              onChange={(_, value) => setYears(value as number[])}
              valueLabelDisplay="auto"
              getAriaLabel={(index) => (index === 0 ? "From year" : "To year")}
              sx={{ width: 160 }}
            />
          </Box>
        )}
      </Box>

      {model.programmes.length > 0 && (
        <Box role="group" aria-label="Programmes on the timeline">
          <Box sx={{ display: "flex" }}>
            <Typography component="h3" sx={{ ...subheading, ...nameCell }}>
              Programmes
            </Typography>
          </Box>
          {programmes.map((mark) => {
            const extent = programmeExtent(mark);
            return (
              <Box key={mark.row.entity.id} sx={{ display: "flex", alignItems: "center", height: ROW }}>
                {/* The name says what its marks say: above it, clear of
                    the row's bar */}
                <Tooltip
                  title={programmeTooltip(mark)}
                  placement="top-start"
                  arrow
                  disableInteractive
                  enterDelay={80}
                  slotProps={RECORD_TOOLTIP_SLOTS}
                >
                  <Box
                    sx={{
                      ...nameCell,
                      fontSize: "0.8125rem",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <EntityLink
                      id={mark.row.entity.id}
                      type={mark.row.entity.type}
                      name={mark.row.entity.name}
                    />
                  </Box>
                </Tooltip>
                <Box sx={{ position: "relative", flexGrow: 1, height: "100%", overflow: "hidden" }}>
                  {gridlines()}
                  {todayLine()}
                  {programmeMarks(mark)}
                  {extent &&
                    hit(
                      extent.left,
                      extent.width,
                      `${mark.row.entity.name ?? ""}, ${programmeDate(mark)}`,
                      programmeTooltip(mark),
                    )}
                </Box>
              </Box>
            );
          })}
          {folded > 0 || allProgrammes ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, height: ROW }}>
              <RowToggle
                open={allProgrammes}
                label={allProgrammes ? "Show fewer programmes" : `Show all ${model.programmes.length} programmes`}
                onToggle={() => setAllProgrammes(!allProgrammes)}
              />
              <Typography variant="caption" sx={quiet}>
                {allProgrammes ? "Fewer programmes" : `${folded} more programmes`}
              </Typography>
            </Box>
          ) : null}
        </Box>
      )}

      {model.lanes.length > 0 && (
        <Box role="group" aria-label="Incidents on the timeline" sx={{ mt: model.programmes.length > 0 ? 1 : 0 }}>
          <Typography component="h3" sx={{ ...subheading, ...nameCell }}>
            Incidents
          </Typography>
          {model.lanes.map((lane) => (
            <Box key={lane.type} sx={{ display: "flex", alignItems: "center", height: LANE }}>
              <Box
                sx={{
                  ...nameCell,
                  fontSize: "0.8125rem",
                  color: lane.type === OTHER_LANE ? "text.disabled" : "text.secondary",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={lane.type}
              >
                {lane.type}
                <Box component="span" sx={{ ...monospaceStyle, ml: 0.75, color: "text.disabled" }}>
                  {lane.marks.length}
                </Box>
              </Box>
              <Box sx={{ position: "relative", flexGrow: 1, height: "100%", overflow: "hidden" }}>
                {gridlines()}
                {todayLine()}
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    top: "50%",
                    borderTop: 1,
                    borderColor: alpha(theme.palette.chart.ink, 0.08),
                  }}
                />
                {lane.marks.map((mark) => {
                  const place = stretch(mark.at.lo, mark.at.hi);
                  return (
                    <React.Fragment key={mark.row.entity.id}>
                      {incidentMark(mark)}
                      {place &&
                        hit(
                          place.left,
                          place.width,
                          `${mark.row.entity.name ?? ""}, ${dateWords(mark.row)}`,
                          <RecordTooltip
                            kind="incident"
                            row={mark.row}
                            hue={incidentHue}
                            roles={mark.roles}
                            dateLabel="Date"
                            date={dateWords(mark.row)}
                            dateNote={precisionNote(mark.at)}
                          />,
                        )}
                    </React.Fragment>
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* The axis: the years beneath the plot, and what has no date */}
      <Box sx={{ display: "flex", alignItems: "flex-start", mt: 0.5 }}>
        <Box sx={nameCell}>
          {(model.undated.programmes > 0 || model.undated.incidents > 0) && (
            <Typography variant="caption" sx={{ color: "text.disabled" }}>
              Not on the timeline, without a date:{" "}
              {[
                model.undated.programmes > 0 &&
                  `${model.undated.programmes} ${model.undated.programmes === 1 ? "programme" : "programmes"}`,
                model.undated.incidents > 0 &&
                  `${model.undated.incidents} ${model.undated.incidents === 1 ? "incident" : "incidents"}`,
              ]
                .filter(Boolean)
                .join(", ")}
            </Typography>
          )}
        </Box>
        {/* The plot's width is measured here, where it always stands */}
        <Box ref={plotRef} sx={{ position: "relative", flexGrow: 1, height: 16 }}>
          {ticks.map((year) => {
            const left = x(Date.UTC(year, 0, 1));
            return left >= 0 && left <= width ? (
              <Typography
                key={year}
                variant="caption"
                sx={{
                  ...monospaceStyle,
                  position: "absolute",
                  left,
                  transform: "translateX(-50%)",
                  fontSize: "0.65625rem",
                  color: "text.disabled",
                }}
              >
                {year}
              </Typography>
            ) : null;
          })}
        </Box>
      </Box>
    </Box>
  );
};

// A bar of the legend, drawn as the plot draws its kind
const LegendBar: React.FC<{
  color: string;
  label: string;
  fade?: boolean;
  hollow?: boolean;
  faint?: boolean;
}> = ({ color, label, fade, hollow, faint }) => (
  <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
    <Box
      component="span"
      sx={{
        width: 22,
        height: BAR,
        borderRadius: `${BAR / 2}px`,
        boxSizing: "border-box",
        ...(hollow
          ? { border: `1.5px dashed ${color}`, backgroundColor: alpha(color, 0.12) }
          : {
              background: fade ? `linear-gradient(to right, ${color} 40%, ${alpha(color, 0)})` : color,
              opacity: faint ? 0.35 : 1,
            }),
      }}
    />
    {label}
  </Box>
);

// A mentioned programme in the legend: the faint band and its dots
const LegendMentions: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
    <Box
      component="span"
      sx={{
        position: "relative",
        width: 30,
        height: BAR,
        borderRadius: `${BAR / 2}px`,
        background: `linear-gradient(to right, ${alpha(color, 0)}, ${alpha(color, 0.22)} 25%, ${alpha(color, 0.22)} 75%, ${alpha(color, 0)})`,
      }}
    >
      {[9, 15, 21].map((left) => (
        <Box
          key={left}
          component="span"
          sx={{
            position: "absolute",
            top: "50%",
            left,
            width: MENTION_DOT,
            height: MENTION_DOT,
            transform: "translate(-50%, -50%)",
            borderRadius: "50%",
            backgroundColor: color,
          }}
        />
      ))}
    </Box>
    {label}
  </Box>
);

// The dots of the legend: the sizes of the severities, smallest first
const LegendDots: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
      {Object.values(SEVERITY_SIZE).map((size) => (
        <Box
          key={size}
          component="span"
          sx={{ width: size, height: size, borderRadius: "50%", backgroundColor: color }}
        />
      ))}
    </Box>
    {label}
  </Box>
);

export default ProductTimeline;
